import React, {useRef, useEffect, useState, useCallback, useMemo} from 'react';

import {debounce, cloneDeep, update} from 'lodash';

import {getClientConfig} from 'ultra/configs/general';
import {isEmptyObj} from "ultra/helpers/utils";

import {Loader} from '@googlemaps/js-api-loader';

// import Autocomplete from '@mui/material/Autocomplete';

import LocationOnIcon from '@mui/icons-material/LocationOn';

import {Collapse} from "@mui/material";

import Grid from '@mui/material/Grid';
// import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';

import {isUndefined} from 'lodash';

import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';

import Preloader from '../../../Preloaders/Preloader';

import './index.scss';

const placeFields = ['id', 'websiteURI', 'location', 'displayName', 'formattedAddress'];

export default function MapContainer(props) {
    const {field, content, defaultZoom, defaultCenter, showControls, value, onModeChange, onBlur, onChange} = props;

    const {firebase} = getClientConfig(window);

    const [googleLibInProgress, setGoogleLibInProgress] = useState(false);

    const [locationGranted, setLocationGranted] = useState();

    const [loading, setLoading] = useState();

    const [wasScrolled, setWasScrolled] = useState(false);

    // const [hasValue, setHasValue] = useState(content?.[field.id]?.lat && content?.[field.id]?.lng);

    const [currentMap, setCurrentMap] = useState();

    const mapRef = useRef(null);

    const controlTypeRef = React.useRef(null);
    const setControlType = data => {
        controlTypeRef.current = data;
    };

    const selectLocationButtonRef = React.useRef(null);
    const setSelectLocationButton = data => {
        selectLocationButtonRef.current = data;
    };

    const closeClickListenerRef = React.useRef(null);
    const setCloseClickListener = data => {
        closeClickListenerRef.current = data;
    };

    const openPlaceInfoRef = React.useRef(null);
    const setOpenPlaceInfo = data => {
        openPlaceInfoRef.current = data;
    };

    const googleMapRef = React.useRef(null);
    const setGoogleMap = data => {
        googleMapRef.current = data;
    };

    const googleLibRef = React.useRef(null);
    const setGoogleLib = data => {
        googleLibRef.current = data;
    };

    const markersRef = React.useRef(null);
    const setMarkers = data => {
        markersRef.current = data;
    };

    const infoWindowRef = React.useRef(null);
    const setInfoWindow = data => {
        infoWindowRef.current = data;
    };

    useEffect(() => {
        const loader = new Loader({
            apiKey: firebase.apiKey,
            version: "weekly",
            libraries: ["places", "marker"]
        });

        setControlType("none");

        setGoogleLibInProgress(true);
        loader.load()
            .then(google => {
                setGoogleLibInProgress(false);

                setGoogleLib(google);

                initMap(firebase.mapId);
                clearMarkers();
            })
            .catch(error => {
                console.error("Error loading Google Maps API", error);
            });


        return () => {
            clearMarkers();
            if (googleLibRef?.current) googleLibRef.current.maps.event.clearListeners(googleMapRef.current, 'click');
        }
    }, []);

    useEffect(() => {
        const hasValue = content?.[field.id]?.lat && content?.[field.id]?.lng;
        // setHasValue(hasValue)

        if (googleMapRef.current) {
            googleLibRef.current.maps.event.clearListeners(googleMapRef.current, 'click');
            googleMapRef.current.addListener("click", handleMapClick);

            let isMarkerPresent = false;
            markersRef?.current?.[field.id]?.map(m => {
                isMarkerPresent = m && content[field.id] && content[field.id].lat === m.position.lat && content[field.id].lng === m.position.lng;
            });
    
            if (!isMarkerPresent && googleMapRef.current) {
                if (!hasValue) {
                    clearMarkers();
                }
                else if (googleLibRef.current && content[field.id]?.lat && content[field.id]?.lng) {
                    setMarker({
                        lat: content[field.id].lat,
                        lng: content[field.id].lng,
                    })
                }
            }
        }
    }, [content[field.id]])

    const clearMarkers = (marker) => {
        markersRef?.current?.[field.id]?.map(m => {
            m.setMap(null);
            m = null;
        });

        const updated = {...markersRef?.current};
        updated[field.id] = marker ? [marker] : [];

        setMarkers(updated);
    }

    const initMap = (mapId, zoom) => {
        // if (googleMapRef?.current) return;

        if (currentMap === mapId) return;

        setCurrentMap(mapId);

        const map = new googleLibRef.current.maps.Map(mapRef.current, {
            mapId: mapId,
            center: defaultCenter,
            gestureHandling: "cooperative",
            clickableIcons: (controlTypeRef?.current === "touch" || !controlTypeRef?.current),
            zoom: zoom || defaultZoom,
            zoomControl: false,
            cameraControl: false,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: (controlTypeRef?.current === "touch" || !controlTypeRef?.current),
            fullscreenControlOptions: {
                position: googleLibRef.current.maps.ControlPosition.BOTTOM_RIGHT
            },
        });

        setGoogleMap(map);

        if (googleLibRef?.current) googleLibRef.current.maps.event.clearListeners(googleMapRef.current, 'click');
        map.addListener("click", handleMapClick);
    }

    const handleMapClick = async (e) => {
        const isIconMouseEvent = (e) => "placeId" in e;

        infoWindowRef?.current?.close();

        if (isIconMouseEvent(e)) {
            const infoWindow = new googleLibRef.current.maps.InfoWindow();

            setInfoWindow(infoWindow);

            infoWindow.setHeaderContent("");
            infoWindow.setContent("");
            infoWindow.close();

            e.stop();

            const place = new googleLibRef.current.maps.places.Place({id: e.placeId});
            await place.fetchFields({fields: placeFields});
            const convertedPlace = convertPlaceToResult(place);
            setOpenPlaceInfo(convertedPlace);
            const position = {lat: convertedPlace.lat, lng: convertedPlace.lng};

            infoWindow.setPosition(position);

            const title = document.createElement("div");
            title.innerHTML = `<b style="font-weight: 700; margin-bottom: 10px; display: block; max-width: 250px;">${convertedPlace.title}</b>`;
            infoWindow.setHeaderContent(title);

            const content = document.createElement("div");
            const style = `style="cursor: pointer; width: 100%; font-size: 14px; margin-bottom: 10px; background: #fff; border: 1px solid #999; border-radius: 5px; padding: 5px 10px;"`;
            content.innerHTML = `<div style="max-width: 250px;">
                <div>
                    <button class="set" type="button" ${style}>Обрати локацію</button>
                </div>
                <div>${convertedPlace.address}</div>
            </div>`;

            // on select
            const button = content.getElementsByClassName("set")[0];
            setSelectLocationButton(button);
            if (button) button?.addEventListener("click", handleSelectLocationClick);

            // on close 
            if (closeClickListenerRef?.current) googleLibRef.current.maps.event.removeListener(closeClickListenerRef?.current);
            const closeClickListener = googleLibRef.current.maps.event.addListener(infoWindow, 'closeclick', () => {
                infoWindow.setMap(null);
                button.removeEventListener('click', handleSelectLocationClick);
                googleLibRef.current.maps.event.removeListener(closeClickListener);
            })
            setCloseClickListener(closeClickListener);

            infoWindow.setContent(content);
            infoWindow.open(googleMapRef?.current);

            onSearchResultSelect(position);
        }
        else if (controlTypeRef?.current === "touch" || !controlTypeRef?.current) {
            onSearchResultSelect(e.latLng.toJSON());
        }
    }

    const handleSelectLocationClick = (e) => {
        e.preventDefault();
        onSearchResultSelect(openPlaceInfoRef?.current);
        infoWindowRef?.current?.close();
    }

    const onSearchResultSelect = (data) => {
        if (isEmptyObj(data)) {
            onChange({target: {value: {}}});
        }
        else {
            const v = {lat: data.lat, lng: data.lng}
            if (data.title) v.title = data.title;
            if (data.address) v.address = data.address;
            // if (data.id) v.link = "https://www.google.com/maps/place/?q=place_id:" + data.id;
            if (data.id) v.googlePlaceID = data.id;
            onChange({target: {value: v}});
        }
    }

    const setMarker = (position) => {
        const marker = new googleLibRef.current.maps.marker.AdvancedMarkerElement({
            map: googleMapRef?.current,
            position,
            gmpDraggable: controlTypeRef?.current !== 'search'
        });

        marker.addListener("dragend", () => {
            onChange({target: {value: {
                lat: marker.position.lat,
                lng: marker.position.lng,
            }}});
        });
    
        if (controlTypeRef?.current === "search" || controlTypeRef?.current === "location") {
            googleMapRef?.current?.setCenter(position);
        }

        if (googleMapRef?.current?.getZoom() < 16) {
            googleMapRef?.current?.setCenter(position);
            googleMapRef?.current?.setZoom(16);
        }

        clearMarkers(marker);
    }

    const scrollToMap = useCallback(() => {
        if (!wasScrolled) {
            let el = window.document.getElementsByClassName("MuiDialogContent-root")[0]
            if (!el) el = window.document.getElementsByClassName("SwipeableDrawerContentWrapper")[0]
            if (el) el.scrollBy(0, 100);
            setWasScrolled(true);
        }
    })

    const onTouchHandler = () => {
        onModeChange("touch");
        setControlType("touch");

        googleMapRef?.current?.setOptions({
            gestureHandling: "cooperative",
            fullscreenControl: true,
            zoomControl: false,
            clickableIcons: true,
        });

        scrollToMap();
        // TODO: CHECK STATE
        // const styledMapType = new googleLibRef.current.maps.StyledMapType(
        //     [
        //         {
        //           featureType: "poi",
        //           stylers: [{ visibility: "on" }],
        //         },
        //       ]
        // );
        // googleMapRef.current.mapTypes.set('styled_map', styledMapType);
        // googleMapRef.current.setMapTypeId('styled_map');

        onChange({target: {value: {}}});

        initMap(firebase.mapId);

        // googleMapRef?.current?.setOptions({
        //     mapId: firebase.mapId,
        // });

        clearMarkers();
    }

    const onSearchHandler = () => {
        onModeChange("search");
        setControlType("search");

        onChange({target: {value: {}}});

        googleMapRef?.current?.setOptions({
            gestureHandling: "none",
            fullscreenControl: false,
            zoomControl: false,
            clickableIcons: false,
        });

        scrollToMap();

        // TODO: CHECK STATE
        // const styledMapType = new googleLibRef.current.maps.StyledMapType(
        //     [
        //         {
        //           featureType: "poi",
        //           stylers: [{ visibility: "on" }],
        //         },
        //       ]
        // );
        // googleMapRef.current.mapTypes.set('styled_map', styledMapType);
        // googleMapRef.current.setMapTypeId('styled_map');

        initMap(firebase.mapId);

        // googleMapRef?.current?.setOptions({
        //     mapId: firebase.mapId,
        // });
    }

    const onLocationHandler = () => {
        onModeChange("location");

        setControlType("location");

        googleMapRef?.current?.setOptions({
            gestureHandling: "none",
            fullscreenControl: false,
            zoomControl: false,
            clickableIcons: false,
        });

        const options = {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0,
        };

        const success = (pos) => {
            setLocationGranted(true);

            // googleMapRef?.current?.setZoom(19);

            const v = {
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
            };

            // const styledMapType = new googleLibRef.current.maps.StyledMapType(
            //     [
            //         {
            //           featureType: "poi",
            //           stylers: [{ visibility: "off" }],
            //         },
            //       ]
            // );
            // googleMapRef.current.mapTypes.set('styled_map', styledMapType);
            // googleMapRef.current.setMapTypeId('styled_map');

            initMap(firebase.mapIdNoIcons, 19);

            // googleMapRef?.current?.setOptions({
            //     mapId: firebase.mapIdNoIcons,
            // });

            onChange({target: {value: v}});

            scrollToMap();
        }

        const error = (err) => {
            setLocationGranted(false);

            console.error(`(${err.code}): ${err.message}`);

            onChange({target: {value: {}}});
        }

        navigator.geolocation.getCurrentPosition(success, error, options);
    }

    // const onClearHandler = () => {
    //     // onTouchHandler();

    //     onChange({target: {value: {}}});
    // }

    return <div className='MapContainer'>
        {googleLibInProgress && <div className='MapPreloader'>
            <div className='MapLoading'>Завантаження Google Maps...</div>
            <Preloader />
        </div>}

        {controlTypeRef?.current === 'location' && (!isUndefined(locationGranted) && !locationGranted) && <p className="LocationGranted">‼️ Для передачі вашої локації, увімкніть передачу геолокації у вашому браузері</p>}

        {showControls &&
            <Grid className='MapControls' container sx={{ alignItems: 'center' }} spacing={2}>
                <Grid item xs={12} md={6}>
                    {/* !isMobile &&  */}
                    <Button disabled={controlTypeRef?.current == "search"} fullWidth variant='outlined' startIcon={<TravelExploreOutlinedIcon />} onClick={onSearchHandler}>
                        &nbsp;Знайти на мапі
                        {/* {!isMobile && <span className="MapControlLabel">Пошук</span>} */}
                        {/* {isMobile && <TravelExploreOutlinedIcon />} */}
                    </Button>
                </Grid>
                <Grid item xs={12} md={6}>
                    {/* !isMobile &&  */}
                    <Button disabled={controlTypeRef?.current == "touch" || !controlTypeRef?.current} fullWidth variant='outlined' startIcon={<TouchAppOutlinedIcon />} onClick={onTouchHandler}>
                        &nbsp;Обрати на мапі
                        {/* {!isMobile && <span className="MapControlLabel">Обрати на мапі</span>} */}
                        {/* {isMobile && <TouchAppOutlinedIcon />} */}
                    </Button>
                </Grid>
                <Grid item xs={12} md={12}>
                    {/* !isMobile &&  */}
                    <Button disabled={controlTypeRef?.current == "location"} fullWidth variant='outlined' startIcon={<MyLocationIcon />} onClick={onLocationHandler}>
                        &nbsp;Передати поточну локацію
                        {/* {!isMobile && <span className="MapControlLabel">Ваша локація</span>} */}
                        {/* {isMobile && <MyLocationIcon />} */}
                    </Button>
                </Grid>
                {/* {!hasValue && <Grid item xs={12} md={6}>
                    <Button fullWidth variant='outlined' className="clearBtn" startIcon={<ClearIcon />} onClick={onClearHandler}>
                        &nbsp;Очистити
                    </Button>
                </Grid>} */}
            </Grid>
        }

        <div className="MapSearchContainer">
            {controlTypeRef?.current === "search" && <Search
                googleLib={googleLibRef?.current}
                onPlaceSelect={onSearchResultSelect}
                setLoading={setLoading}
                value={value}
            />}
            {loading && <div className='MapResultPreloader'><Preloader /></div>}

            <Collapse in={controlTypeRef?.current === "location" || controlTypeRef?.current === "touch" || controlTypeRef?.current === "search"}>
                <div className={googleLibInProgress ? 'Map' : 'Map Loaded'} ref={mapRef} />
            </Collapse>        
        </div>
    </div>
};

const Search = (props) => {
    const {showErrorText, setLoading, onPlaceSelect, googleLib, value} = props;

    const [searchQuery, setSearchQuery] = useState(value?.title || "");
    const [suggestionsResults, setSuggestionsResults] = useState([]);
    
    // const [cache, setCache] = useState({});
    const cache = {}

    const fetchPredictions = useCallback(
        async (inputValue) => {
            // console.log("inputValue", inputValue)
            if (!googleLib || !inputValue) return;

            const {suggestions} = await googleLib.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                input: inputValue,
                sessionToken: new googleLib.maps.places.AutocompleteSessionToken()
            })

            // console.log("suggestions", suggestions)

            const promises = [];
            suggestions.map(async (suggestion) => {
                promises.push(new Promise(async (resolve, reject) => {
                    const placePrediction = suggestion.placePrediction;
        
                    const place = placePrediction.toPlace();
                    await place.fetchFields({fields: placeFields});
                    // console.log("place", place)
                    resolve(convertPlaceToResult(place));
                }))                
            })

            const result = await Promise.all(promises);

            // setOptions(inputValue, [
            //     {
            //         address: "Jl. Raya Pengosekan Ubud, Ubud, Kecamatan Ubud, Kabupaten Gianyar, Bali 80571, Indonesia",
            //         id: "ChIJJUZcVXQ90i0RGDGzIXI3Py8",
            //         lat: -8.5228782,
            //         lng: 115.26288249999999,
            //         title: "TEST: MaxOneHotel.com @ Ubud",
            //     },
            //     {
            //         address: "fdsakfjsa",
            //         id: "432842098209384302",
            //         lat: -2.5228782,
            //         lng: 13.26288249999999,
            //         title: "flsdkaflsaj",
            //     },
            //     {
            //         address: "fdsak4234fjsa",
            //         id: "4328420982093843asfadsfas02",
            //         lat: -6.5228782,
            //         lng: 15.26288249999999,
            //         title: "42342342",
            //     }
            // ]);
            setOptions(inputValue, result);
        },
        [googleLib]
    );

    const onInputChange = useCallback((e) => {
        if (e?.target?.value?.length === 0) {
            setOptions(undefined, [])
        }
        else {
            setLoading(true);
        }
        setSearchQuery(e.target.value);
        getOptions(e);
    }, [fetchPredictions]);

    const getOptions = debounce((e) => { 
        if (e?.target?.value?.length > 2) {
            if (cache[e.target.value]) {
                // console.log("from cache")
                setOptions(e.target.value, cache[e.target.value]);
            } else {
                // console.log("request")
                // console.log("cache", cache)
                fetchPredictions(e?.target?.value);
            }
        }
    }, 1000)

    function setOptions(request, options) {
        // console.log("setOptions", request, options)
        if (request && !cache[request]) {
            // console.log("set cache", request, options)
            // const updatedCache = {...cache};
            cache[request] = options;
            // console.log("updatedCache", updatedCache)
            // setCache(updatedCache);
        }

        setSuggestionsResults(options);
        setLoading(false);
    }
    
    function onSearchResultItemClick(index) {
        const newValue = suggestionsResults[index];
        onPlaceSelect(newValue);
        setOptions(undefined, []);
        setSearchQuery(suggestionsResults[index].title)
    }

    function onCleanSearchHandler() {
        onPlaceSelect({});
        setOptions(undefined, []);
        setSearchQuery("");
    }

    return <div className='Search'>
        <FormControl fullWidth variant="outlined">
            <InputLabel>Пошук локації</InputLabel>
            <OutlinedInput
                // {...params}
                value={searchQuery}
                error={Boolean(showErrorText)}
                onChange={onInputChange}
                fullWidth
                autoFocus
                endAdornment={
                    <InputAdornment position="end">
                        <IconButton
                            onClick={onCleanSearchHandler}
                            edge="end"
                        >
                            <CloseIcon className='CloseIcon'/>
                        </IconButton>
                    </InputAdornment>
                }
            />
        </FormControl>

        {suggestionsResults.length > 0 && <ul className='SearchResults'>
            {suggestionsResults.map((option, i) =>
                <li className="SearchResultItem" key={"autocomplete_" + option.id} onClick={() => onSearchResultItemClick(i)}>
                    <Grid container sx={{ alignItems: 'center' }}>
                        <Grid item sx={{ display: 'flex', width: 44 }}>
                            <LocationOnIcon sx={{ color: 'text.secondary' }} />
                        </Grid>
                        <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                            <div className='LocationSearchResultItem'>
                                <div className='title'>
                                    {option.title}
                                </div>
                                <div className='details'>
                                    {option.address}
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </li>)}
        </ul>}
    </div>}

        {/* <Autocomplete
            value={value}
            onInputChange={onInputChange}

            autoComplete
            includeInputInList
            freeSolo

            clearIcon={<></>}
            clearOnBlur={false}

            noOptionsText="Відсутні локації"
            options={suggestionsResults}
            // isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.title || ""}

            onChange={
                (e, newValue) => {
                    if (!newValue) {
                        onPlaceSelect({});
                    }
                    else {
                        onPlaceSelect(newValue);
                    }
                }
            }

            renderInput={(params) => <TextField
                {...params}
                label="Пошук локації"
                error={Boolean(showErrorText)}
                fullWidth
                autoFocus
            />}

            renderOption={(props, option) => {
                console.log("renderOption", option)
                const {key, ...optionProps} = props;
                return <li key={"autocomplete_" + option.id} {...optionProps}>
                    <Grid container sx={{ alignItems: 'center' }}>
                        <Grid item sx={{ display: 'flex', width: 44 }}>
                            <LocationOnIcon sx={{ color: 'text.secondary' }} />
                        </Grid>
                        <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                            <div className='LocationSearchResultItem'>
                                <div className='title'>
                                    {option.title}
                                </div>
                                <div className='details'>
                                    {option.address}
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </li>
            }}
        /> */}
// </div>
// }

const convertPlaceToResult = (place) => {
    return {
        id: place.id,
        title: place.displayName,
        address: place.formattedAddress,
        lat: place.location.lat(),
        lng: place.location.lng()
    }
}
